import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Paper,
  Typography,
} from "@mui/material";
import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DATE_FORMAT, FORMAT_HOUR_FULL } from "../../../config";
import { PayModules } from "../../../containers/WaitingRoomPage/PayModules";
import { NewOrder, PaymentHistory } from "../../../services/dto/Common";
import { User } from "../../../services/dto/Security";
import UserService from "../../../services/userService";
import { getErrorString, getName, useIsMounted } from "../../../services/utils";
import { loadPaymentHistory } from "../../../store/actions/payment";
import { IAppState } from "../../../store/reducers";
import { AuthState } from "../../../store/reducers/auth";
import { PaymentState } from "../../../store/reducers/payment";
import { ProfileIcon } from "../../ProfileIcon";
import "./styles.scss";

const PER_PAGE = 2;

export const Orders = () => {
  const { authUser } = useSelector<IAppState, AuthState>((s) => s.auth);
  const { history } = useSelector<IAppState, PaymentState>((s) => s.payment);
  const dispatch = useDispatch();
  const isMounted = useIsMounted();

  const [orders, setOrders] = useState<NewOrder[]>([]);
  const [index, setIndex] = useState(0);
  const [payOrder, setPayOrder] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string>();
  const items = _.uniqBy([...orders, ...(history.data || [])], (i) => i.id);

  useEffect(() => {
    if (!authUser) return;
    loadData();
  }, [authUser]);

  const loadData = () => {
    dispatch(loadPaymentHistory({}));
    setLoading(true);
    UserService.getOrders({
      paid: false,
      patientId: authUser?.id || "",
    })
      .then((rsp) => {
        const orders = rsp.data.filter(
          (order) => order.patientResponsibility > 0,
        );
        if (isMounted.current) setOrders(orders);
      })
      .catch((e) => {
        if (isMounted.current) setErr(getErrorString(e));
      });
  };

  const renderPrevNext = () => {
    const isLoading = (loading || history.loading) && !items;
    const isHasErr = !!history.err || !!err || _.isEmpty(items);

    if (isLoading) {
      return (
        <Typography variant="body2" color="common.black">
          Loading...
        </Typography>
      );
    } else if (isHasErr) {
      return null;
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "40px",
        }}
      >
        <Box sx={{ borderBottom: "1px solid #E0E0E0" }} />
        <Box sx={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
          <Button
            variant="text"
            disabled={index <= 0}
            onClick={() => setIndex(index - 1)}
          >
            Prev
          </Button>
          <Box sx={{ borderLeft: "1px solid #E0E0E0", height: "100%" }} />
          <Button
            variant="text"
            disabled={PER_PAGE * (index + 1) >= items?.length}
            onClick={() => setIndex(index + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  };

  const renderUser = (user: User) => {
    if (!user) return null;
    return (
      <Card elevation={0} sx={{ backgroundColor: "background.default" }}>
        <CardHeader
          sx={{ padding: "8px" }}
          avatar={
            <Avatar>
              <ProfileIcon user={user} color={"white"} />
            </Avatar>
          }
          title={getName(user)}
          subheader={(user.providerInfo?.qualifications || []).join(", ")}
        />
      </Card>
    );
  };

  const getOrderName = (item: NewOrder | PaymentHistory) => {
    if ("category" in item) {
      return item.category === "HST"
        ? "Home Sleep Test"
        : item.category === "In-Lab Study"
          ? "In Lab Sleep Study"
          : item.name;
    }
    return item.reason;
  };

  const renderItem = () => {
    const renderedItems = items.slice(PER_PAGE * index, PER_PAGE * (index + 1));

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {renderedItems.map((item, i) => {
          // only show 2 items
          const isOrder = "category" in item; // otherwise it's a payment history
          const isBaseOrder = isOrder && item.category;
          const showPayButton =
            isBaseOrder && item.patientResponsibility > 0 && !item.paid;
          const orderName = getOrderName(item);

          const payAmount = isOrder
            ? item.patientResponsibility
            : item.amount / 100;

          return (
            <Box
              key={`payment-history${i}`}
              sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <Typography variant="h6" color="common.black">
                {orderName}
              </Typography>
              <Typography variant="body2" color="common.black">
                {moment(item.createdAt).format(
                  DATE_FORMAT + " " + FORMAT_HOUR_FULL,
                )}
              </Typography>
              {isBaseOrder && renderUser(item.provider)}
              <Box
                sx={{ display: "flex", gap: "16px", justifyContent: "right" }}
              >
                {showPayButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setPayOrder(item)}
                  >
                    Pay Now
                  </Button>
                )}
                <Typography
                  variant="h6"
                  color="common.black"
                  sx={{ textAlign: "right" }}
                >
                  ${payAmount.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Paper elevation={0} style={{ padding: "22px 32px", height: "548px" }}>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          height: "548px",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Payment History
        </Typography>
        {_.isEmpty(items) ? (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Typography variant="body1" color="common.black">
              No Past Payments
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>{renderItem()}</Box>
        )}
        {renderPrevNext()}
        {payOrder && (
          <PayModules
            amount={payOrder.patientResponsibility}
            entityId={payOrder.id}
            description={payOrder.name}
            type={"order"}
            showModal={_.isEmpty(authUser?.creditCards) ? "new" : "exist"}
            onClose={(card) => {
              if (card) {
                loadData();
                setIndex(0);
              }
              setPayOrder(undefined);
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default Orders;
